// Browser-side, data-only patching for the USA Metropolismania 2 raw MODE2/2352 image.
//
// The bridge never ships the game. A mod pack contains only replacement bytes
// authored by us plus their offsets inside files on the user's locally selected
// disc image. At boot time those file-relative edits are translated into raw CD
// sector offsets and applied to a temporary in-memory File.

export type Mm2FilePatch = {
  file: "PACKAGE.BIN" | "ROOM.BIN";
  offset: number;
  bytes: Uint8Array;
  label: string;
  expected?: Uint8Array;
};

export type Mm2ModPack = {
  id: string;
  name: string;
  description: string;
  patches: Mm2FilePatch[];
};

const RAW_SECTOR_SIZE = 2352;
const USER_DATA_OFFSET = 24;
const USER_DATA_SIZE = 2048;
const EDC_OFFSET = 0x818;
const ECC_P_OFFSET = 0x81c;
const ECC_Q_OFFSET = 0x8c8;

const FILE_LBA: Record<Mm2FilePatch["file"], number> = {
  "PACKAGE.BIN": 797,
  "ROOM.BIN": 13380,
};

const eccForward = new Uint8Array(256);
const eccBackward = new Uint8Array(256);
const edcTable = new Uint32Array(256);

for (let i = 0; i < 256; i += 1) {
  const j = ((i << 1) ^ ((i & 0x80) ? 0x11d : 0)) & 0xff;
  eccForward[i] = j;
  eccBackward[i ^ j] = i;
  let edc = i;
  for (let bit = 0; bit < 8; bit += 1) edc = (edc >>> 1) ^ ((edc & 1) ? 0xd8018001 : 0);
  edcTable[i] = edc >>> 0;
}

function rawOffsetForFileByte(file: Mm2FilePatch["file"], fileOffset: number): number {
  if (!Number.isInteger(fileOffset) || fileOffset < 0) throw new RangeError(`Invalid ${file} offset: ${fileOffset}`);
  const sector = Math.floor(fileOffset / USER_DATA_SIZE);
  const withinSector = fileOffset % USER_DATA_SIZE;
  return (FILE_LBA[file] + sector) * RAW_SECTOR_SIZE + USER_DATA_OFFSET + withinSector;
}

function logicalBytes(image: Uint8Array, file: Mm2FilePatch["file"], offset: number, length: number): Uint8Array {
  const result = new Uint8Array(length);
  let targetOffset = 0;
  let fileOffset = offset;
  while (targetOffset < length) {
    const withinSector = fileOffset % USER_DATA_SIZE;
    const readable = Math.min(USER_DATA_SIZE - withinSector, length - targetOffset);
    const rawOffset = rawOffsetForFileByte(file, fileOffset);
    if (rawOffset + readable > image.length) throw new RangeError(`Read beyond selected disc image while validating ${file}`);
    result.set(image.subarray(rawOffset, rawOffset + readable), targetOffset);
    targetOffset += readable;
    fileOffset += readable;
  }
  return result;
}

function assertExpectedBytes(image: Uint8Array, patch: Mm2FilePatch): void {
  if (!patch.expected) return;
  const actual = logicalBytes(image, patch.file, patch.offset, patch.expected.length);
  for (let i = 0; i < actual.length; i += 1) {
    if (actual[i] !== patch.expected[i]) throw new Error(`${patch.label}: selected image does not match the MM2 USA bytes expected by this mod pack at ${patch.file}+0x${patch.offset.toString(16)}`);
  }
}

function computeEdc(sector: Uint8Array, start: number, length: number): number {
  let edc = 0;
  for (let i = start; i < start + length; i += 1) edc = (edc >>> 8) ^ edcTable[(edc ^ sector[i]) & 0xff];
  return edc >>> 0;
}

function computeEccBlock(sector: Uint8Array, sourceOffset: number, majorCount: number, minorCount: number, majorMult: number, minorInc: number, destinationOffset: number): void {
  const size = majorCount * minorCount;
  for (let major = 0; major < majorCount; major += 1) {
    let index = (major >> 1) * majorMult + (major & 1);
    let eccA = 0;
    let eccB = 0;
    for (let minor = 0; minor < minorCount; minor += 1) {
      const value = sector[sourceOffset + index];
      index += minorInc;
      if (index >= size) index -= size;
      eccA ^= value;
      eccB ^= value;
      eccA = eccForward[eccA];
    }
    eccA = eccBackward[eccForward[eccA] ^ eccB];
    sector[destinationOffset + major] = eccA;
    sector[destinationOffset + major + majorCount] = eccA ^ eccB;
  }
}

function regenerateMode2Form1Sector(image: Uint8Array, lba: number): void {
  const base = lba * RAW_SECTOR_SIZE;
  const end = base + RAW_SECTOR_SIZE;
  if (base < 0 || end > image.length) throw new RangeError(`Cannot regenerate raw sector ${lba}: outside selected image`);
  const sector = image.subarray(base, end);
  if (sector[15] !== 0x02) throw new Error(`MM2 patch touched LBA ${lba}, but it is not a MODE2 sector`);
  if ((sector[18] & 0x20) !== 0 || (sector[22] & 0x20) !== 0) throw new Error(`MM2 patch touched LBA ${lba}, but it is not MODE2/Form1`);
  const edc = computeEdc(sector, 0x10, 0x808);
  sector[EDC_OFFSET] = edc & 0xff;
  sector[EDC_OFFSET + 1] = (edc >>> 8) & 0xff;
  sector[EDC_OFFSET + 2] = (edc >>> 16) & 0xff;
  sector[EDC_OFFSET + 3] = (edc >>> 24) & 0xff;
  const address = sector.slice(12, 16);
  sector.fill(0, 12, 16);
  computeEccBlock(sector, 0x0c, 86, 24, 2, 86, ECC_P_OFFSET);
  computeEccBlock(sector, 0x0c, 52, 43, 86, 88, ECC_Q_OFFSET);
  sector.set(address, 12);
}

function applyOnePatch(image: Uint8Array, patch: Mm2FilePatch, touchedLbas: Set<number>): void {
  assertExpectedBytes(image, patch);
  let sourceOffset = 0;
  let fileOffset = patch.offset;
  while (sourceOffset < patch.bytes.length) {
    const sectorWithinFile = Math.floor(fileOffset / USER_DATA_SIZE);
    const withinSector = fileOffset % USER_DATA_SIZE;
    const writable = Math.min(USER_DATA_SIZE - withinSector, patch.bytes.length - sourceOffset);
    const rawOffset = rawOffsetForFileByte(patch.file, fileOffset);
    if (rawOffset + writable > image.length) throw new RangeError(`${patch.label} would write beyond the selected disc image`);
    image.set(patch.bytes.subarray(sourceOffset, sourceOffset + writable), rawOffset);
    touchedLbas.add(FILE_LBA[patch.file] + sectorWithinFile);
    sourceOffset += writable;
    fileOffset += writable;
  }
}

export async function applyMm2ModPack(source: File, pack: Mm2ModPack): Promise<File> {
  const image = new Uint8Array(await source.arrayBuffer());
  const touchedLbas = new Set<number>();
  for (const patch of pack.patches) applyOnePatch(image, patch, touchedLbas);
  touchedLbas.forEach((lba) => regenerateMode2Form1Sector(image, lba));
  return new File([image], source.name, { type: source.type || "application/octet-stream", lastModified: Date.now() });
}

export function describeMm2ModPack(pack: Mm2ModPack): string {
  const byteCount = pack.patches.reduce((total, patch) => total + patch.bytes.length, 0);
  return `${pack.name}: ${pack.patches.length} edits / ${byteCount} replacement bytes`;
}
